/**
 * Утилиты недельного календаря для модуля задач.
 *
 * Все даты — локальные (toLocalIsoDate). Никаких UTC-сравнений.
 */

export interface WeekDay {
  /** YYYY-MM-DD (локальная дата). */
  iso: string;
  /** Сокращённое название дня недели по-русски. */
  weekdayShort: string;
  /** Полное название дня недели. */
  weekdayFull: string;
  /** Число месяца. */
  dayOfMonth: number;
  /** Месяц (русский, родительный падеж). */
  monthGenitive: string;
  isToday: boolean;
  isWeekend: boolean;
}

const WEEKDAY_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const WEEKDAY_FULL = [
  "Воскресенье",
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
];
const MONTH_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

export function toLocalIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayIsoLocal(): string {
  return toLocalIsoDate(new Date());
}

/** Дата ISO YYYY-MM-DD → Date (локальный полночь). */
export function parseIsoLocal(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Понедельник той недели, в которой находится дата (ISO 8601 — неделя начинается с пн). */
export function startOfWeekMonday(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = out.getDay(); // 0 = вс, 1 = пн, …, 6 = сб
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  return out;
}

export function addDays(d: Date, days: number): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + days);
  return out;
}

export function buildWeek(anchor: Date): WeekDay[] {
  const monday = startOfWeekMonday(anchor);
  const today = todayIsoLocal();
  return Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    const iso = toLocalIsoDate(d);
    const dow = d.getDay();
    return {
      iso,
      weekdayShort: WEEKDAY_SHORT[dow]!,
      weekdayFull: WEEKDAY_FULL[dow]!,
      dayOfMonth: d.getDate(),
      monthGenitive: MONTH_GENITIVE[d.getMonth()]!,
      isToday: iso === today,
      isWeekend: dow === 0 || dow === 6,
    };
  });
}

/** Заголовок диапазона недели, например «27 апр. – 3 мая 2026». */
export function formatWeekRange(week: WeekDay[]): string {
  if (week.length === 0) return "";
  const first = week[0]!;
  const last = week[week.length - 1]!;
  const firstD = parseIsoLocal(first.iso);
  const lastD = parseIsoLocal(last.iso);
  const sameMonth = firstD.getMonth() === lastD.getMonth();
  const sameYear = firstD.getFullYear() === lastD.getFullYear();

  const firstMonthShort = MONTH_GENITIVE[firstD.getMonth()]!.slice(0, 3);
  const lastMonthShort = MONTH_GENITIVE[lastD.getMonth()]!.slice(0, 3);

  if (sameMonth) {
    return `${first.dayOfMonth} – ${last.dayOfMonth} ${lastMonthShort}. ${lastD.getFullYear()}`;
  }
  if (sameYear) {
    return `${first.dayOfMonth} ${firstMonthShort}. – ${last.dayOfMonth} ${lastMonthShort}. ${lastD.getFullYear()}`;
  }
  return `${first.dayOfMonth} ${firstMonthShort}. ${firstD.getFullYear()} – ${last.dayOfMonth} ${lastMonthShort}. ${lastD.getFullYear()}`;
}

export function isSameWeek(a: Date, b: Date): boolean {
  return toLocalIsoDate(startOfWeekMonday(a)) === toLocalIsoDate(startOfWeekMonday(b));
}
